import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, User, Menu, X } from "lucide-react";
import { useAuth, useCart } from "@/App";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const navigate = useNavigate();

  const cartCount = cart.items.reduce((sum, item) => sum + item.quantity, 0);

  const navLinks = [
    { name: "Shop", href: "/shop" },
    { name: "Clothing", href: "/shop/clothing" },
    { name: "Accessories", href: "/shop/accessories" },
    { name: "The Garage", href: "/suggestions" },
  ];

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-heavy" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <div className="relative">
              <span className="text-2xl sm:text-3xl font-bold tracking-tighter text-[#39FF14] neon-text heading">
                GCSR
              </span>
              <span className="absolute -bottom-1 left-0 text-[8px] text-[#888] tracking-[0.3em] label-text">
                GRIP CITY
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className="nav-link"
                data-testid={`nav-${link.name.toLowerCase().replace(" ", "-")}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-4">
            {/* Cart */}
            <Link
              to="/cart"
              className="relative p-2 text-[#EDEDED] hover:text-[#39FF14] transition-colors"
              data-testid="cart-link"
            >
              <ShoppingBag size={24} />
              {cartCount > 0 && (
                <span className="cart-badge" data-testid="cart-count">{cartCount}</span>
              )}
            </Link>

            {/* User Menu */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="p-2 text-[#EDEDED] hover:text-[#39FF14] transition-colors"
                    data-testid="user-menu-trigger"
                  >
                    <User size={24} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 bg-[#0A0A0A] border-[#333]">
                  <div className="px-3 py-2">
                    <p className="text-sm text-[#888]">Signed in as</p>
                    <p className="text-sm font-medium text-[#EDEDED] truncate">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator className="bg-[#333]" />
                  <DropdownMenuItem
                    className="cursor-pointer text-[#EDEDED] focus:bg-[#1a1a1a] focus:text-[#39FF14]"
                    onClick={() => navigate("/profile")}
                    data-testid="profile-link"
                  >
                    My Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer text-[#EDEDED] focus:bg-[#1a1a1a] focus:text-[#39FF14]"
                    onClick={() => navigate("/profile")}
                  >
                    Order History
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-[#333]" />
                  <DropdownMenuItem
                    className="cursor-pointer text-red-500 focus:bg-[#1a1a1a] focus:text-red-400"
                    onClick={handleLogout}
                    data-testid="logout-btn"
                  >
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link
                to="/auth"
                className="p-2 text-[#EDEDED] hover:text-[#39FF14] transition-colors"
                data-testid="login-link"
              >
                <User size={24} />
              </Link>
            )}

            {/* Mobile Menu Toggle */}
            <button
              className="md:hidden p-2 text-[#EDEDED]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${mobileMenuOpen ? "open" : ""}`}>
        <button
          className="absolute top-6 right-6 text-[#EDEDED]"
          onClick={() => setMobileMenuOpen(false)}
        >
          <X size={28} />
        </button>
        <div className="flex flex-col gap-6">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.href}
              className="text-2xl text-[#EDEDED] hover:text-[#39FF14] transition-colors heading"
              onClick={() => setMobileMenuOpen(false)}
            >
              {link.name}
            </Link>
          ))}
          {!user && (
            <Link
              to="/auth"
              className="text-2xl text-[#EDEDED] hover:text-[#39FF14] transition-colors heading"
              onClick={() => setMobileMenuOpen(false)}
            >
              Sign In
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
};
